import { Card } from "@material-ui/core";
import Container from "@saleor/components/Container";
import CouponFilterBar from "@saleor/components/CouponFilterBar";
import PageBackNavigation from "@saleor/components/PageBackNavigation";
import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation/PageBreadcrumbNavigation";
import PageHeader from "@saleor/components/PageHeader";
import {
  useCouponManualReorder,
  useCouponPresetReorder
} from "@saleor/coupons/mutations";
import { Coupons_coupons_edges_node } from "@saleor/coupons/types/Coupons";
import useNotifier from "@saleor/hooks/useNotifier";
import { sectionNames } from "@saleor/intl";
import { FilterPageProps, PageListProps, TabPageProps } from "@saleor/types";
import { ApolloQueryResult } from "apollo-client";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import CouponRankingList, {
  PresetReorderDetails,
  ReorderDetails
} from "../CouponRankingList/CouponRankingList";
import { CouponListUrlFilterOpts, createFilterStructure } from "./filters";
import useCouponRankingListPageStyles from "./styles";

export interface CouponRankingListPageProps
  extends PageListProps,
    TabPageProps,
    FilterPageProps<undefined, CouponListUrlFilterOpts> {
  coupons: Coupons_coupons_edges_node[];
  onBack: () => void;
  couponManageUrl: string;
  onReorder: (reorderDetails: ReorderDetails) => void;
  onPresetReorder: (presetReorderDetails: PresetReorderDetails) => void;
  refetch: () => Promise<ApolloQueryResult<any>>;
}

export const CouponRankingListPage: React.FC<CouponRankingListPageProps> = ({
  onSearchChange,
  currencySymbol,
  onTabSave,
  initialSearch,
  tabs,
  onTabChange,
  onTabDelete,
  currentTab,
  coupons,
  onBack,
  couponManageUrl,
  refetch
}) => {
  const [lastMovedCouponId, setLastMovedCouponId] = useState<string | null>(
    null
  );

  const breadcrumbDirectory = [
    {
      name: "Coupons",
      url: couponManageUrl
    },
    {
      name: "Coupon Ranking"
    }
  ];

  const classes = useCouponRankingListPageStyles({});
  const notify = useNotifier();
  const [reorderCoupons] = useCouponManualReorder({
    onCompleted: data => {
      if (data.couponManualReorder.errors.length === 0) {
        return null;
      } else {
        notify({
          status: "error",
          text: data.couponManualReorder.errors[0].message
        });
      }
    },
    onError: error => {
      notify({
        status: "error",
        text: error.message
      });
    }
  });

  const handleReorder = async (reorderDetails: ReorderDetails) => {
    const { movedCouponId, beforeItemId, afterItemId } = reorderDetails;

    const couponOrderGroups = {
      coupons: [movedCouponId],
      insertAfter: beforeItemId,
      insertBefore: afterItemId
    };

    try {
      await reorderCoupons({
        variables: { couponOrderGroups: [couponOrderGroups] }
      });
    } catch (error) {
      return null;
    }
  };

  const [presetReorderCoupons] = useCouponPresetReorder({
    onCompleted: data => {
      if (data.couponPresetReorder.errors.length === 0) {
        notify({
          status: "success",
          text: "Coupon reordered successfully"
        });
        refetch();
        setLastMovedCouponId(data.couponPresetReorder.coupon.id);
      } else {
        notify({
          status: "error",
          text: data.couponPresetReorder.errors[0].message
        });
      }
    },
    onError: error => {
      notify({
        status: "error",
        text: error.message
      });
    }
  });

  const handlePresetReordering = async (
    presetReorderDetails: PresetReorderDetails
  ) => {
    const { couponId, reorderPreset } = presetReorderDetails;
    try {
      await presetReorderCoupons({
        variables: {
          input: {
            couponId,
            reorderPreset
          }
        }
      });
    } catch (error) {
      notify({
        status: "error",
        text: error.message
      });
    }
  };

  const intl = useIntl();

  const structure = createFilterStructure();
  return (
    <Container>
      <PageBreadcrumbNavigation items={breadcrumbDirectory} />
      <div className={classes.headerContainer}>
        <PageBackNavigation onBack={onBack} />
        <PageHeader title={intl.formatMessage(sectionNames.couponRanking)} />
      </div>
      <Card>
        <>
          <CouponFilterBar
            currencySymbol={currencySymbol}
            currentTab={currentTab}
            initialSearch={initialSearch}
            tabs={tabs}
            filterStructure={structure}
            searchPlaceholder={intl.formatMessage({
              defaultMessage: "Search Coupons"
            })}
            onSearchChange={onSearchChange}
            onFilterChange={undefined}
            onTabChange={onTabChange}
            onTabDelete={onTabDelete}
            disablePlusOneTab={true}
            onTabSave={onTabSave}
          />
          <CouponRankingList
            coupons={coupons}
            onReorder={handleReorder}
            onPresetReorder={handlePresetReordering}
            lastMovedCouponId={lastMovedCouponId}
          />
        </>
      </Card>
    </Container>
  );
};

export default CouponRankingListPage;
